export const AUTH_EXCHANGE_TIMEOUT_MS = 30_000;

export type AuthExchangeInput = {
  method: 'GET' | 'POST';
  path: string;
  jsonBody?: unknown;
  bearerToken?: string;
};

export type AuthExchangeResult<T> = {
  ok: boolean;
  status: number;
  data: T | null;
  networkError?: unknown;
};

export const authExchangeFetch = async <T>(
  input: AuthExchangeInput,
): Promise<AuthExchangeResult<T>> => {
  const baseUrl = `${import.meta.env.VITE_FF_REST_API_URL}/v4`;

  // This controller is deliberately NOT tied to any Vue effect scope — the
  // refresh-token exchange must survive route changes / component unmounts
  // The only thing that can abort it is our own timeout.
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), AUTH_EXCHANGE_TIMEOUT_MS);

  try {
    const url = new URL(`${baseUrl}${input.path}`).toString();

    const headers: Record<string, string> = {
      'Content-Type': 'application/json; charset=utf-8',
    };
    if (input.bearerToken) {
      headers.Authorization = `Bearer ${input.bearerToken}`;
    }

    const response = await fetch(url, {
      method: input.method,
      headers,
      body: input.jsonBody !== undefined ? JSON.stringify(input.jsonBody) : undefined,
      signal: controller.signal,
    });

    let data: T | null = null;
    try {
      data = (await response.json()) as T;
    } catch {
      data = null;
    }

    return { ok: response.ok, status: response.status, data };
  } catch (err: unknown) {
    return { ok: false, status: 0, data: null, networkError: err };
  } finally {
    clearTimeout(timer);
  }
};
